import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Chip,
  Avatar,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  Dashboard, 
  LocationOn, 
  Star,
  People,
  Schedule
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useAuth } from '../contexts/AuthContext';
import { GET_AVAILABLE_STORES } from '../graphql/queries';
import { StaffStore } from '../types';
import StoreAvatar from '../components/common/StoreAvatar';

const Stores = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // Query to get available stores for the current user
  const { data, loading, error } = useQuery(GET_AVAILABLE_STORES, {
    skip: !user, // Skip if no user is logged in
  });
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading stores: {error.message}
        </Alert>
      </Box>
    );
  }
  
  const stores = data?.availableStores || [];

  // If no stores found, show empty state
  if (stores.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No stores found. You are not assigned to any stores yet!
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
              Your Stores
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your barbershop locations and access their dashboards
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {stores.map((store: any, index: number) => (
            <Grid item xs={12} md={6} lg={4} key={store.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: theme.shadows[8],
                      transform: 'translateY(-4px)',
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <StoreAvatar
                        storeAvatar={store.avatar}
                        storeName={store.name}
                        sx={{ 
                          width: 60, 
                          height: 60, 
                          mr: 2,
                          bgcolor: theme.palette.primary.main 
                        }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          {store.name}
                        </Typography>
                        <Chip
                          label={store.isDeleted ? 'Inactive' : 'Active'}
                          color={store.isDeleted ? 'default' : 'success'}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      </Box>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {store.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <LocationOn sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {store.address}
                      </Typography>
                    </Box>

                    {/* Staff Members Section */}
                    {store.staff && store.staff.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                          Staff Members
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {store.staff.slice(0, 3).map((staffStore: StaffStore) => (
                            <Chip
                              key={staffStore.id}
                              label={staffStore.staff.fullName}
                              size="small"
                              variant="outlined"
                              avatar={
                                <Avatar sx={{ width: 24, height: 24 }}>
                                  {staffStore.staff.firstName?.charAt(0) || staffStore.staff.fullName.charAt(0)}
                                </Avatar>
                              }
                            />
                          ))}
                          {store.staff.length > 3 && (
                            <Chip
                              label={`+${store.staff.length - 3} more`}
                              size="small"
                              color="primary"
                            />
                          )}
                        </Box>
                      </Box>
                    )}

                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Star sx={{ fontSize: 20, color: theme.palette.warning.main }} />
                          <Typography variant="body2" fontWeight={600}>
                            {store.rating != null ? Number(store.rating).toFixed(1) : 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Rating
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <People sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                          <Typography variant="body2" fontWeight={600}>
                            {store.staff?.length || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Staff
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Schedule sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                          <Typography variant="body2" fontWeight={600}>
                            {store.network?.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Network
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      startIcon={<Dashboard />}
                      component={Link}
                      to={`/store/${store.id}/dashboard`}
                      fullWidth
                      disabled={store.isDeleted}
                    >
                      Open Dashboard
                    </Button>
                  </CardActions>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </motion.div>
    </Box>
  );
};

export default Stores;
